import type { FinancialMetricData } from '@/hooks/react-query/useFinancialMetrics';
import type { UserSummary } from './user';

// Enums - Defined first to be used in interfaces
export enum PortfolioCompanyStatus {
  ACTIVE = 'ACTIVE',
  EXITED = 'EXITED',
  WRITTEN_OFF = 'WRITTEN_OFF',
  UNDER_REVIEW = 'UNDER_REVIEW',
  ARCHIVED = 'ARCHIVED',
}

export enum FundInvestmentRows {
  INCLUDE = 'include',
  EXCLUDE = 'exclude',
}

export enum InvestmentType {
  EQUITY = 'equity',
  DEBT = 'debt',
  CONVERTIBLE = 'convertible',
  SAFE = 'safe',
  OTHER = 'other',
}

export enum ValuationMethod {
  COST = 'cost',
  DCF = 'dcf',
  REVENUE_MULTIPLE = 'revenue_multiple',
  EBITDA_MULTIPLE = 'ebitda_multiple',
  COMPARABLE_COMPANIES = 'comparable_companies',
  TRANSACTION_MULTIPLE = 'transaction_multiple',
  OTHER = 'other',
}

export enum ValuationSource {
  MANUAL = 'manual',
  AUTOMATIC = 'automatic',
}

export enum ValuationStatus {
  PROPOSED = 'proposed',
  APPROVED = 'approved',
  WITHDRAWN = 'withdrawn',
}

export enum ReportingPeriodType {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  ANNUALLY = 'annually',
  OTHER = 'other',
}

enum DocumentType {
  INVESTMENT_MEMO = 'investment_memo',
  BOARD_MATERIALS = 'board_materials',
  FINANCIAL_STATEMENTS = 'financial_statements',
  LEGAL_DOCUMENTS = 'legal_documents',
  VALUATION_REPORTS = 'valuation_reports',
  OTHER = 'other',
}

export enum ContactRole {
  CEO = 'ceo',
  CTO = 'cto',
  CFO = 'cfo',
  COO = 'coo',
  FOUNDER = 'founder',
  BOARD_MEMBER = 'board_member',
  INVESTOR_RELATIONS = 'investor_relations',
  HEAD_OF_SALES = 'head_of_sales',
  HEAD_OF_MARKETING = 'head_of_marketing',
  HEAD_OF_PRODUCT = 'head_of_product',
  HEAD_OF_ENGINEERING = 'head_of_engineering',
  VP_SALES = 'vp_sales',
  VP_MARKETING = 'vp_marketing',
  VP_PRODUCT = 'vp_product',
  VP_ENGINEERING = 'vp_engineering',
  GENERAL_COUNSEL = 'general_counsel',
  HEAD_OF_HR = 'head_of_hr',
  OTHER = 'other',
}

// Interfaces
export interface PortfolioCompany {
  id: string;
  fund_id: string;
  fund?: {
    id: string;
    name: string;
    display_name?: string;
    base_currency?: string;
  };
  name: string;
  legal_name?: string;
  sector?: string;
  sub_sector?: string;
  country_code?: string;
  currency?: string;
  description?: string;
  logo_url?: string;
  website?: string;
  initial_investment_date: string;
  investment_type: InvestmentType;
  current_status: PortfolioCompanyStatus;
  status_date?: string | null;
  total_invested: number;
  current_valuation?: number;
  latest_valuation_date?: string;
  ownership_percentage?: number;
  total_fund_ownership_percentage?: number;
  cost_basis: number;
  receiving_fund_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  managers: UserSummary[];
}

export interface PortfolioCompanyListItem {
  id: string;
  name: string;
  sector?: string;
  investment_type: InvestmentType;
  country_code?: string;
  currency?: string;
  fund_base_currency?: string;
  initial_investment_date: string;
  total_invested: number;
  current_valuation?: number;
  ownership_percentage?: number;
  current_status: PortfolioCompanyStatus;
  latest_valuation_date?: string;
  fund_id: string;
  fund_name?: string;
  receiving_fund_id?: string | null;
  managers: UserSummary[];
}

export type PortfolioCompanySortField =
  | 'name'
  | 'sector'
  | 'investment_type'
  | 'country_code'
  | 'initial_investment_date'
  | 'current_valuation'
  | 'fund_name';

export type SortOrder = 'asc' | 'desc';

export interface PortfolioCompanyListParams {
  fund_id?: string;
  status?: PortfolioCompanyStatus;
  sector?: string;
  search?: string;
  sort_by?: PortfolioCompanySortField;
  sort_order?: SortOrder;
  page?: number;
  page_size?: number;
  fund_investments?: FundInvestmentRows;
}

export interface PortfolioCompanyPage {
  items: PortfolioCompanyListItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface PortfolioSummary {
  total_companies: number;
  active_companies: number;
  exited_companies: number;
  total_invested: number;
  current_portfolio_value: number;
  realized_value: number;
  unrealized_gain_loss: number;
  total_return: number;
}

export interface PortfolioValuation {
  id: string;
  portfolio_company_id: string;
  valuation_date: string;
  valuation_amount: number;
  valuation_method?: ValuationMethod;
  revenue_multiple?: number;
  ebitda_multiple?: number;
  discount_rate?: number;
  notes?: string;
  is_current: boolean;
  valuation_source: ValuationSource;
  source_transaction_id?: string;
  status: ValuationStatus;
  approved_by?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface PortfolioFinancialMetrics {
  id: string;
  portfolio_company_id: string;
  reporting_date: string;
  period_type: ReportingPeriodType;
  revenue?: number;
  gross_profit?: number;
  ebitda?: number;
  net_income?: number;
  cash_balance?: number;
  total_assets?: number;
  total_liabilities?: number;
  arr?: number;
  mrr?: number;
  customer_count?: number;
  ltv_cac_ratio?: number;
  gross_margin_percentage?: number;
  custom_metrics?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export type FinancialMetricsImportIssueReason =
  | 'unrecognised_metric'
  | 'metric_not_configured'
  | 'ambiguous_metric'
  | 'duplicate_metric'
  | 'unrecognised_period'
  | 'period_not_found'
  | 'duplicate_period'
  | 'invalid_value';

export type FinancialMetricsImportRecordStatus = 'valid' | 'invalid';

export type FinancialMetricsImportMode = 'validate' | 'apply';

export type FinancialMetricValue = number | string;

export interface FinancialMetricsImportRecord {
  cell: string;
  status: FinancialMetricsImportRecordStatus;
  metric: string;
  period: string;
  data_type: FinancialMetricData | null;
  file_value: string;
  current_value: FinancialMetricValue | null;
  new_value: FinancialMetricValue | null;
  reason: FinancialMetricsImportIssueReason | null;
  message: string | null;
}

export interface FinancialMetricsImportResult {
  mode: FinancialMetricsImportMode;
  valid_records: number;
  invalid_records: number;
  overwritten_values: number;
  affected_periods: number;
  records: FinancialMetricsImportRecord[];
}

export interface PortfolioDocument {
  id: string;
  portfolio_company_id: string;
  document_name: string;
  document_type: DocumentType;
  file_path?: string;
  file_size?: number;
  mime_type?: string;
  upload_date: string;
  description?: string;
  tags?: string[];
  is_confidential: boolean;
  accessible_roles?: string[];
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface PortfolioCompanyDetail extends PortfolioCompany {
  valuations: PortfolioValuation[];
  financial_metrics: PortfolioFinancialMetrics[];
  documents: PortfolioDocument[];
  contacts: PortfolioContact[];
}

// Fund-level valuations summary
interface FundValuationCompanyItem {
  id: string;
  name: string;
  investment_type: InvestmentType;
  current_valuation: number | null;
  latest_valuation_date: string | null;
}

export interface FundValuationsSummary {
  total_portfolio_value: number;
  company_count: number;
  companies: FundValuationCompanyItem[];
}

// Create/Update types
export interface CreatePortfolioCompanyRequest {
  fund_id: string;
  name: string;
  legal_name?: string;
  sector?: string;
  sub_sector?: string;
  country_code?: string;
  currency?: string;
  description?: string;
  logo_url?: string;
  website?: string;
  initial_investment_date: string;
  investment_type?: InvestmentType;
  current_status?: PortfolioCompanyStatus;
  current_valuation?: number;
  latest_valuation_date?: string;
  cost_basis?: number;
  manager_ids?: string[];
}

export interface UpdatePortfolioCompanyRequest {
  name?: string;
  legal_name?: string;
  sector?: string;
  sub_sector?: string;
  country_code?: string;
  currency?: string;
  description?: string;
  logo_url?: string;
  website?: string;
  initial_investment_date?: string;
  investment_type?: InvestmentType;
  current_status?: PortfolioCompanyStatus;
  status_date?: string | null;
  current_valuation?: number;
  latest_valuation_date?: string;
  ownership_percentage?: number;
  manager_ids?: string[];
}

export interface CreateValuationRequest {
  valuation_date: string;
  valuation_amount: number;
  valuation_method?: ValuationMethod;
  revenue_multiple?: number;
  ebitda_multiple?: number;
  discount_rate?: number;
  notes?: string;
  is_current?: boolean;
}

export interface CreateFinancialMetricsRequest {
  reporting_date: string;
  period_type: ReportingPeriodType;
  revenue?: number;
  gross_profit?: number;
  ebitda?: number;
  net_income?: number;
  cash_balance?: number;
  total_assets?: number;
  total_liabilities?: number;
  arr?: number;
  mrr?: number;
  customer_count?: number;
  ltv_cac_ratio?: number;
  gross_margin_percentage?: number;
  custom_metrics?: Record<string, unknown>;
  portfolio_company_id: string;
}

export interface CreateDocumentRequest {
  document_name: string;
  document_type: DocumentType;
  file_path?: string;
  file_size?: number;
  mime_type?: string;
  upload_date: string;
  description?: string;
  tags?: string[];
  is_confidential?: boolean;
  accessible_roles?: string[];
}

// Bank account interfaces
export interface PortfolioCompanyBankAccount {
  id: string;
  portfolio_company_id: string;
  account_name: string;
  bank_name?: string | null;
  /** ISO 3166-1 alpha-3 country code. */
  bank_country?: string | null;
  iban?: string | null;
  swift_bic?: string | null;
  account_number?: string | null;
  currency: string;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

/**
 * One account in the set submitted for a company. An `id` names an account the
 * company already holds; without one the account is new.
 */
export interface BankAccountWriteRequest {
  id?: string;
  account_name: string;
  bank_name?: string | null;
  bank_country?: string | null;
  iban?: string | null;
  swift_bic?: string | null;
  account_number?: string | null;
  currency: string;
  is_primary: boolean;
}

// Contact interfaces
export interface PortfolioContact {
  id: string;
  portfolio_company_id: string;
  first_name: string;
  last_name: string;
  role: ContactRole;
  title?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  twitter_url?: string;
  bio?: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface CreateContactRequest {
  first_name: string;
  last_name: string;
  role: ContactRole;
  title?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  twitter_url?: string;
  bio?: string;
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
  is_primary?: boolean;
}

export interface UpdateContactRequest {
  first_name?: string;
  last_name?: string;
  role?: ContactRole;
  title?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  twitter_url?: string;
  bio?: string;
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
  is_primary?: boolean;
}

// Types for dynamic financial metrics display
export interface FormattedMetric {
  name: string;
  displayName: string;
  value: string;
  category: string;
  dataType: string;
  unit?: string;
  noLongerCollected?: boolean;
}

export type DeletionBlockerEntity =
  | 'fund_transactions'
  | 'deal_costs'
  | 'asset_sale_transactions'
  | 'captable_ledger_transactions'
  | 'captable_convertible_instruments'
  | 'scenarios'
  | 'contacts';

export interface DeletionBlocker {
  entity: DeletionBlockerEntity;
  total_count: number;
  blocking_count: number;
}

export interface DeletionEligibility {
  deletable: boolean;
  blockers: DeletionBlocker[];
}

export const DELETION_BLOCKER_LABELS: Record<
  DeletionBlockerEntity,
  { singular: string; plural: string; statusAware: boolean }
> = {
  fund_transactions: {
    singular: 'fund transaction',
    plural: 'fund transactions',
    statusAware: true,
  },
  deal_costs: {
    singular: 'deal cost',
    plural: 'deal costs',
    statusAware: false,
  },
  asset_sale_transactions: {
    singular: 'asset sale transaction',
    plural: 'asset sale transactions',
    statusAware: true,
  },
  captable_ledger_transactions: {
    singular: 'cap-table transaction',
    plural: 'cap-table transactions',
    statusAware: true,
  },
  captable_convertible_instruments: {
    singular: 'convertible instrument',
    plural: 'convertible instruments',
    statusAware: true,
  },
  scenarios: {
    singular: 'cap-table scenario',
    plural: 'cap-table scenarios',
    statusAware: false,
  },
  contacts: {
    singular: 'contact',
    plural: 'contacts',
    statusAware: false,
  },
};
